import { CenterItem, RowAlignedItems } from '@components/LinearLayout';
import {
  InputFieldsType,
  designTypeFields,
  orderedFields,
} from '@composites/InboundButton/utils';
import {
  ACCOUNT_ID,
  usePublicAttendees,
  usePublicPortfolioByAccount,
} from '@utils/apiHooks';
import { printPdf, useMobileWidth } from '@utils/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@components/Button';
import WeModal from '@components/WeModal';
import { ApplyButtonWrapper } from '@composites/Filters/styles';
import { DefaultBooleanValues } from '@composites/InboundButton';
import AttendeeList from '@pages/AttendeeList';
import { getEventDetails } from '@store/slices/eventSlice';
import { SPEAKER_PAGE_WIDTH } from '@utils/constants';
import { safeJSONParse } from '@utils/fn';
import { useInbound } from '@utils/Inbound/useInbound';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import RegistrationForm from '../RegistrationForm';

interface DownloadAttendeeListProps {
  label: string;
  buttonType: string;
  onClick?: () => void;
  eventId?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
}

const DownloadAttendeeList = ({
  label,
  onClick,
  buttonType,
  eventId,
  inboundValues,
}: DownloadAttendeeListProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [showModal, setShowModal] = useState<boolean>(false);
  const [checkedApproval, setCheckedApproval] = useState<boolean>(false);
  const isMobile = useMobileWidth();
  const dispatch = useDispatch();
  const { inboundCookie, actionInbound, registerInbound, shouldShowForm } =
    useInbound();

  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });
  const { data } = usePublicAttendees({
    eventId: sluggedEvent?.id,
    enabled: sluggedEvent?.id?.length > 0,
  });

  const account = useMemo(
    () => portfolios?.data?.[0]?.account,
    [portfolios?.data]
  );

  const generateAttendeeListPDF = useCallback(() => {
    const doc = (
      <AttendeeList
        event={sluggedEvent}
        theme={theme}
        account={account}
        attendees={data?.attendees}
      />
    );
    printPdf(doc, `${t('attendee-list')} - ${sluggedEvent?.name}`).catch(
      console.error
    );
  }, [account, data?.attendees, sluggedEvent, t, theme]);

  const inputElements = useMemo(
    () =>
      orderedFields
        .map(item => {
          if (inboundValues?.booleanValues?.[item]?.visible) {
            const designTypeParameters = designTypeFields?.find(
              e => e.id === item
            );
            return {
              ...designTypeParameters,
              ...inboundValues?.booleanValues?.[item],
            };
          }
          return undefined;
        })
        ?.filter(item => item) as InputFieldsType,
    [inboundValues?.booleanValues]
  );

  const defaultValues: Record<string, string> = useMemo(() => {
    return inputElements.reduce<Record<string, string>>((acc, key) => {
      acc[key.id] = safeJSONParse(inboundCookie)?.[key.id] ?? '';
      return acc;
    }, {});
  }, [inboundCookie, inputElements]);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      if (!checkedApproval) return;

      await registerInbound(
        {
          ...data,
          event: sluggedEvent?.id,
          path: window.location.href,
          action: 'download_attendee_list',
        },
        () => {
          generateAttendeeListPDF();
          setShowModal(false);
        }
      );
    },
    [
      checkedApproval,
      dispatch,
      inboundCookie,
      registerInbound,
      sluggedEvent?.id,
    ]
  );
  return (
    <>
      <Button
        title={label}
        backgroundColor={sluggedEvent?.design?.themeColor}
        hoverBackgroundColor={theme.color.mobilityGrey}
        textColor={theme.color.white}
        lineHeight={theme.lineHeight.height125}
        fontSize={theme.fontSize.size20}
        borderRadius={theme.borderRadius.radius10}
        padding="1.1rem 3rem"
        width={isMobile ? '100%' : 'max-content'}
        onClick={() => {
          void (async () => {
            if (shouldShowForm) {
              setShowModal(true);
            } else {
              generateAttendeeListPDF();

              await actionInbound({
                ...JSON.parse(inboundCookie ?? '{}'),
                event: sluggedEvent?.id,
                path: window.location.href,
                action: 'download_attendee_list',
              });
            }
          })();
        }}
      />
      {showModal && (
        <WeModal
          visible={showModal}
          maxHeight="94vh"
          backgroundColor={theme.color.pureWhite}
          width={`min(${SPEAKER_PAGE_WIDTH}, 90vw)`}
          onClose={() => {
            setShowModal(false);
          }}
          footer={
            <CenterItem>
              <ApplyButtonWrapper>
                <RowAlignedItems $gap="3rem">
                  <Button
                    title={t('download')}
                    backgroundColor={sluggedEvent?.design.themeColor}
                    hoverBackgroundColor={theme.color.mobilityGrey}
                    textColor={theme.color.white}
                    borderRadius={theme.borderRadius.radius10}
                    padding="0.6rem 2.5rem"
                    width="max-content"
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={handleSubmit(onSubmit)}
                  />
                </RowAlignedItems>
              </ApplyButtonWrapper>
            </CenterItem>
          }
        >
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <RegistrationForm
              control={control}
              errors={errors}
              inputElements={inputElements}
              setCheckedApproval={setCheckedApproval}
              checkedApproval={checkedApproval}
            />
          </form>
        </WeModal>
      )}
    </>
  );
};

export default DownloadAttendeeList;
